import { removeNonLetters } from "./text-cleaning.utils";

/**
 * מסיר את המילה האחרונה מהטקסט.
 * מטפל במקרים של טקסט ריק או רווחים בלבד.
 *
 * @param text מחרוזת המקור שממנה יש להסיר את המילה האחרונה
 * @returns המחרוזת ללא המילה האחרונה
 */
export const removeLastWordFromText = (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText) return '';
    return trimmedText.split(' ').slice(0, -1).join(' ');
};


// Clean a string and return it as an array of words
/**
 * מנקה מחרוזת ומחזירה מערך מילים.
 *
 * @param str מערך המחרוזות שיש לאחד ולנקות
 * @param [withNonLetters=false] האם להשאיר תווים שאינם אותיות
 * @returns מערך המילים לאחר הניקוי
 */
export const getCleanStringAsArray = (
    str: string[],
    withNonLetters?: boolean
): string[] =>
    removeNonLetters(str?.join(' '), withNonLetters)
        .split(' ')
        .filter((el) => el && el !== '\n');

// פונקציה להמרת מספרים למילים בעברית
/**
 * פונקציה להמרת מספרים למילים בעברית.
 *
 * @param num המספר להמרה
 * @returns המחרוזת המילולית המתארת את המספר
 */
export const numberToWords = (num: number): string => {
    if (isNaN(num)) return '';
    if (num === 0) return 'אפס';

    const units = ['', 'אחת', 'שתיים', 'שלוש', 'ארבע', 'חמש', 'שש', 'שבע', 'שמונה', 'תשע'];
    const teens = ['עשר', 'אחת עשרה', 'שתים עשרה', 'שלוש עשרה', 'ארבע עשרה', 'חמש עשרה', 'שש עשרה', 'שבע עשרה', 'שמונה עשרה', 'תשע עשרה'];
    const tens = ['', 'עשר', 'עשרים', 'שלושים', 'ארבעים', 'חמישים', 'שישים', 'שבעים', 'שמונים', 'תשעים'];
    const hundreds = ['', 'מאה', 'מאתיים', 'שלוש מאות', 'ארבע מאות', 'חמש מאות', 'שש מאות', 'שבע מאות', 'שמונה מאות', 'תשע מאות'];
    const thousands = ['', 'אלף', 'אלפיים', 'שלושת אלפים', 'ארבעת אלפים', 'חמשת אלפים', 'ששת אלפים', 'שבעת אלפים', 'שמונת אלפים', 'תשעת אלפים'];

    if (num < 10) {
        return units[num];
    } else if (num < 20) {
        return teens[num - 10];
    } else if (num < 100) {
        const unit = num % 10;
        const ten = Math.floor(num / 10);
        return unit ? `${tens[ten]} ו${units[unit]}` : tens[ten];
    } else if (num < 1000) {
        const hundred = Math.floor(num / 100);
        const remainder = num % 100;

        if (remainder === 0) {
            return hundreds[hundred];
        } else {
            return `${hundreds[hundred]} ${numberToWords(remainder)}`;
        }
    } else if (num < 10000) {
        const thousand = Math.floor(num / 1000);
        const remainder = num % 1000;

        if (remainder === 0) {
            return thousands[thousand];
        } else {
            return `${thousands[thousand]} ${numberToWords(remainder)}`;
        }
    } else {
        return num.toString(); // למספרים גדולים מדי, השאר אותם כמספרים
    }
};

/**
 * מוסיף תגית <br/> לאחר נקודותיים או נקודות המופיעות בסוף מילה או לפני רווח/סוף שורה.
 *
 * @param text הטקסט לעיבוד
 * @returns הטקסט עם תגיות <br/> במקום המתאים
 */
export const addBrAfterDotsAndColons = (text: string): string =>
    text.replace(/([.:])(?=\s|$)/g, "$1<br/>");

/**
 * מעביר סימני פיסוק שמופיעים אחרי </span> פנימה אל תוך התגית
 * ומוודא שיש רווח אחרי סימני הפיסוק.
 *
 * @param text הטקסט לעיבוד
 * @returns הטקסט לאחר תיקון מיקום סימני הפיסוק
 */
export const movePunctuationInsideSpan = (text: string): string =>
    text.replace(/(<\/span>)([,.:;!?״׳"']+)(\S)/g, "$2$1 $3")
        .replace(/(<\/span>)([,.:;!?״׳"']+)(?!\s)/g, "$2$1 ")
        .replace(/(<\/span>)([,.:;!?״׳"']+)(\s)/g, "$2$1$3");

/**
 * מסיר רווחים לפני סימני פיסוק בתוך תגיות span.
 *
 * דוגמה: <span>"רֵאשִׁית" ?</span> -> <span>"רֵאשִׁית"?</span>
 *
 * @param text הטקסט לעיבוד
 * @returns הטקסט לאחר הסרת הרווחים המיותרים
 */
export const removePunctuationSpacesInsideSpan = (text: string): string =>
    text.replace(/(<span[^>]*>[^<]*)\s+([.,;:!?״׳"']+)([^<]*<\/span>)/g, '$1$2$3');

/**
 * מתקן תגיות <br> לא תקינות (כמו <br>משהו</br>) ומשאיר רק <br/>
 * כמו כן מטפל בתגיות br עם תכונות או תוכן מיותר.
 *
 * @param text הטקסט לעיבוד
 * @returns הטקסט לאחר תיקון תגיות ה־br
 */
export const fixBrTags = (text: string): string =>
    text
        // מחליף תגיות br סגורות עם תוכן ב-<br/> + התוכן
        .replace(/<br[^>]*>([^<]*)<\/br>/gi, '<br/>$1')
        // מחליף תגיות br עם תכונות אבל ללא תוכן ב-<br/> פשוט
        .replace(/<br[^>]*>/gi, '<br/>')
        // מחליף תגיות br שמכילות תווים או רווחים פנימיים
        .replace(/<br[^>]*\s+[^>]*>/gi, '<br/>')
        // מחליף תגיות br לא סגורות שמכילות תוכן
        .replace(/<br>([^<]+)/gi, '<br/>$1');

/**
* מפצל מחרוזת סביב סימני פיצול (אתנחתא ונקודותיים) ומחזיר מערך מקטעים.
* אם אין סימני פיצול – מוחזר מערך המכיל את הטקסט המקורי בלבד.
*
* @param text הטקסט לעיבוד
* @returns מערך מקטעי הטקסט לאחר הפיצול
*/

export const splitTextByDelimiters = (text: string): string[] => {
    const token = '§§SPLIT§§';
    
    // מחפש אתנחתא ונקודותיים ומוסיף טוקן אחריהם
    const withToken = text
        // פיצול אחרי אתנחתא
        .replace(/(\S*)\u0591(\S*)/g, `$1\u0591$2${token}`)
        // פיצול אחרי נקודותיים - רק בתוכן טקסטואלי, לא בתגיות
        .replace(/>([^<]*):([^<]*)</g, `>$1:${token}$2<`)
        // פיצול אחרי נקודותיים בסוף הטקסט
        .replace(/>([^<]*):(\s*)$/g, `>$1:${token}$2`)
        // פיצול אחרי נקודותיים בתחילת הטקסט (אם אין תגיות)
        .replace(/^([^<]*):(\s|$)/g, `$1:${token}$2`);
    
    const parts = withToken.split(token).map(part => replaceNbspWithSpace(part).trim()).filter(part => part.length > 0);
    return parts.length > 1 ? parts : [text];
}

/**
 * מחליף ישויות HTML של רווח קשיח (&nbsp;) ברווח רגיל.
 * שימושי לניקוי טקסטים שמגיעים עם ישויות כאלה כדי למנוע תקלות בשבירת שורה.
 *
 * @param text הטקסט המקורי
 * @returns הטקסט לאחר ההמרה
 */
export const replaceNbspWithSpace = (text: string): string => text.replace(/&nbsp;/g, ' ');

/**
 * מחלץ את הקלאס מתגית HTML
 * 
 * @param line שורת HTML
 * @returns הקלאס שנמצא בתגית או מחרוזת ריקה אם לא נמצא
 */
export const extractClassName = (line: string): string => {
  const classMatch = line.match(/<[^>]*class="([^"]*)"[^>]*>/);
  return classMatch?.[1] || '';
};

/**
 * מחלץ את התוכן הפנימי של תגית p אם קיימת, אחרת מחזיר את הליין כמו שהוא
 * 
 * @param line שורת HTML שעשויה להכיל תגית p
 * @returns התוכן הפנימי של תגית p או השורה המלאה אם אין תגית p
 */
export const extractLineContent = (line: string): string => {
  const trimmedLine = line.trim();
  
  // בדיקה אם השורה מתחילה ב-<p> ומסתיימת ב-</p>
  const pTagMatch = trimmedLine.match(/^<p(?:\s[^>]*)?>(.+)<\/p>$/i);
  
  if (pTagMatch) {
    return pTagMatch[1]; // מחזיר את התוכן הפנימי
  }
  
  return line; // מחזיר את השורה כמו שהיא
};
